import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle, Eye, EyeOff, Lock } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Toast from "../../common/Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import { POST } from "../../service/CRUDService";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import { automationTestIds } from "../../testing/automationTestIds";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import AuthBrandHeader from "../../components/AuthBrandHeader/AuthBrandHeader";

const EXPIRED_LINK_FALLBACK = "The reset link is invalid or expired.";

const ForgotPasswordAccount = () => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [validPassword, setValidPassword] = useState(false);
  const [errorHeading, setErrorHeading] = useState("");
  const [dataMessage, setDataMessage] = useState("");
  const [isExpiredLink, setIsExpiredLink] = useState(false);
  const [autoOpenTriggered, setAutoOpenTriggered] = useState(false);
  const [modalOpenedOnce, setModalOpenedOnce] = useState(false);
  const [showManualSignIn, setShowManualSignIn] = useState(false);
  const successTimeoutRef = useRef(null);

  const { uuid, token } = useParams();

  const validationSchema = Yup.object({
    password: Yup.string()
      .required(`${t("authPages:password_required")}`)
      .matches(/^(?=.*[A-Z])/, `${t("authPages:password_uppercase")}`)
      .matches(/^(?=.*[a-z])/, `${t("authPages:password_lowercase")}`)
      .matches(/^(?=.*\d)/, `${t("authPages:must_contain_number")}`)
      .matches(
        /^(?=.*[~`!@#$%^&*()_\-+={[}\]|:;"'<,>.?/])/,
        `${t("authPages:must_contain_special_char")}`,
      )
      .min(8, `${t("authPages:password_min_length")}`),
    confirm_password: Yup.string()
      .required(`${t("authPages:confirm_password_required")}`)
      .oneOf([Yup.ref("password")], `${t("authPages:passwords_mismatch")}`),
  });

  const openSignInModal = () => {
    authContext.chooseSignUp_SignIn(true);
    authContext.setTriggerLoginModuleToggleValue(true);
  };

  const updatePassword = async (uuid, token, password) => {
    try {
      const response = await POST(EntityConstants.POST_UPDATE_PASSWORD, {
        uuid,
        token,
        password,
      });
      const data = await response.json();
      if (response.ok) {
        const successMessage =
          data?.message || t("resetpassword:success_message");

        Toast({
          type: "success",
          title: t("authPages:success"),
          message: successMessage,
        });

        setInvalidPassword(false);
        setIsExpiredLink(false);
        setValidPassword(true);
        setErrorHeading("");
        setDataMessage(successMessage);
        setAutoOpenTriggered(false);
        setModalOpenedOnce(false);
        setShowManualSignIn(false);
        successTimeoutRef.current = setTimeout(() => {
          openSignInModal();
          setAutoOpenTriggered(true);
        }, 1500);
      } else {
        const errorHeadingValue = data?.error || "error";
        const errorMessageValue =
          data?.message || data?.error || t("authPages:password_update_error");
        const normalizedError = String(errorMessageValue || "").toLowerCase();
        const isExpiredError =
          (normalizedError.includes("expired") &&
            (normalizedError.includes("link") ||
              normalizedError.includes("token") ||
              normalizedError.includes("reset"))) ||
          (normalizedError.includes("invalid") &&
            (normalizedError.includes("link") ||
              normalizedError.includes("token") ||
              normalizedError.includes("reset")));

        Toast({
          type: "error",
          title: t("authPages:error"),
          message: errorMessageValue,
        });

        if (isExpiredError) {
          setIsExpiredLink(true);
          setInvalidPassword(false);
          setValidPassword(false);
          setErrorHeading(errorHeadingValue);
          setDataMessage(errorMessageValue || EXPIRED_LINK_FALLBACK);
          return;
        }

        setIsExpiredLink(false);
        setInvalidPassword(true);
        setValidPassword(false);
        setErrorHeading(errorHeadingValue);
        setDataMessage(errorMessageValue);
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("authPages:error"),
        message: t("authPages:password_update_error"),
      });
    }
  };

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!validPassword || !autoOpenTriggered) {
      return;
    }

    if (authContext.triggerLoginModuleToggleValue) {
      if (!modalOpenedOnce) {
        setModalOpenedOnce(true);
      }
      return;
    }

    if (modalOpenedOnce && !authContext.isLoggedIn) {
      setShowManualSignIn(true);
    }
  }, [
    validPassword,
    autoOpenTriggered,
    modalOpenedOnce,
    authContext.triggerLoginModuleToggleValue,
    authContext.isLoggedIn,
  ]);

  return (
    <div
      className="w-full min-h-screen bg-cfSecondary flex flex-col"
      id={pageRootTestIds.forgotPassword}
    >
      <AuthBrandHeader />
      <div
        className={`w-full m-auto px-4 py-8 ${
          isExpiredLink || validPassword ? "max-w-2xl" : "max-w-lg"
        }`}
      >
        {!isExpiredLink && !validPassword && (
          <div className="flex flex-col items-center text-center mb-7">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-cfDisabledLight rounded-full blur-xl"></div>
                <div className="relative p-[1.5rem] rounded-full bg-cfDisabledLight border-[0.1rem] border-cfDisabled">
                  <Lock className="w-7 h-7 text-cfPrimary" />
                </div>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-3 text-balance">
              {t("authPages:change_password")}
            </h1>
            <p className="text-cfSignInSignUpIntroMessageText text-base">
              {t("authPages:enter_new_password")}
            </p>
          </div>
        )}

        <div
          id={automationTestIds.forgotPasswordCard}
          className={`bg-card border-1 rounded-xl shadow-lg backdrop-blur-sm overflow-hidden ${
            isExpiredLink
              ? "bg-white border-[#d0d5dd] rounded-[2rem] shadow-[0_8px_24px_rgba(16,24,40,0.08)]"
              : validPassword
                ? "bg-white border-[#d0d5dd] rounded-[2rem] shadow-[0_8px_24px_rgba(16,24,40,0.08)]"
                : "border-[#b1b7f2]"
          }`}
        >
          {!validPassword && !isExpiredLink ? (
            <Formik
              initialValues={{ password: "", confirm_password: "" }}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                return updatePassword(uuid, token, values.password);
              }}
            >
              {({ values, isSubmitting }) => (
                <Form id={automationTestIds.forgotPasswordForm}>
                  <div className="flex flex-col">
                    <div className="p-6 md:p-8 md:border-r md:border-border">
                      <div className="space-y-3">
                        <label
                          htmlFor={automationTestIds.forgotPasswordNewPassword}
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                        >
                          {t("authPages:new_password")}
                        </label>
                        <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                          <Field
                            type={showNewPassword ? "text" : "password"}
                            name="password"
                            id={automationTestIds.forgotPasswordNewPassword}
                            placeholder={t("authPages:new_password")}
                            className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full bg-transparent"
                          />
                          <button
                            type="button"
                            id={automationTestIds.forgotPasswordToggleNewVisibility}
                            className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                            aria-label={
                              showNewPassword
                                ? t("resetpassword:hide_password")
                                : t("resetpassword:show_password")
                            }
                            onClick={() => setShowNewPassword((prev) => !prev)}
                          >
                            {showNewPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        <ErrorMessage
                          name="password"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      <div className="space-y-3 mt-4">
                        <label
                          htmlFor={automationTestIds.forgotPasswordConfirm}
                          className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                        >
                          {t("authPages:confirm_password")}
                        </label>
                        <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                          <Field
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirm_password"
                            id={automationTestIds.forgotPasswordConfirm}
                            placeholder={t("authPages:confirm_password")}
                            className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full bg-transparent"
                          />
                          <button
                            type="button"
                            id={automationTestIds.forgotPasswordToggleConfirmVisibility}
                            className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                            aria-label={
                              showConfirmPassword
                                ? t("resetpassword:hide_password")
                                : t("resetpassword:show_password")
                            }
                            onClick={() =>
                              setShowConfirmPassword((prev) => !prev)
                            }
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        <ErrorMessage
                          name="confirm_password"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      {invalidPassword && dataMessage && (
                        <p className="text-red-500 text-sm mt-4">
                          {dataMessage}
                        </p>
                      )}

                      <div className="grid grid-cols-1 gap-4 mt-6 w-full">
                        <button
                          type="submit"
                          id={automationTestIds.forgotPasswordSubmit}
                          disabled={
                            isSubmitting ||
                            !values.password ||
                            !values.confirm_password
                          }
                          className="rounded-[8px] text-base font-semibold bg-cfButton hover:bg-cfPrimaryDark h-[3rem] px-2 text-cfSecondary focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {t("authPages:change_password")}
                        </button>
                      </div>
                    </div>

                    <div className="p-6 md:p-8 bg-cfSearchContainer flex flex-col">
                      <div className="flex items-center gap-2 mb-3">
                        <p className="text-md font-bold text-foreground">
                          {t("resetpassword:requirements_title")}
                        </p>
                      </div>
                      <ul className="list-disc pl-2 text-sm text-cfSignInSignUpInputPlaceholderText space-y-2 md:space-y-3">
                        <li>{t("resetpassword:requirement_min_length")}</li>
                        <li>
                          {t("resetpassword:requirement_mixed_characters")}
                        </li>
                        <li>
                          {t("resetpassword:requirement_allowed_characters")}
                        </li>
                        <li>{t("resetpassword:requirement_no_repeat")}</li>
                      </ul>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          ) : validPassword ? (
            <div className="p-8 md:p-10 text-center bg-white">
              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full bg-[#E8F5EC] flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-[#12B76A]" />
                </div>
              </div>
              <h2 className="mt-6 text-2xl font-semibold text-[#101828]">
                {t("authPages:password_updated_successfully")}
              </h2>
              <p className="mx-auto mt-4 max-w-3xl text-lg leading-[1.4] text-[#667085]">
                {dataMessage}
              </p>
              {showManualSignIn && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    id={automationTestIds.forgotPasswordSignIn}
                    className="rounded-[8px] focus:outline-none text-md font-semibold bg-[#2648B8] hover:bg-[#1f3d9a] px-4 py-2.5 text-white"
                    onClick={openSignInModal}
                  >
                    {t("authPages:sign_in")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 md:p-10 text-center bg-white">
              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full bg-[#fee4e2] flex items-center justify-center">
                  <AlertTriangle className="w-10 h-10 text-[#D92D20]" />
                </div>
              </div>
              <h2 className="mt-6 text-2xl font-semibold text-[#101828] text-ellipsis truncate">
                {errorHeading}
              </h2>
              <p className="mx-auto mt-4 max-w-3xl text-lg leading-[1.4] text-[#667085]">
                {dataMessage}
              </p>
              <div className="mt-8 flex justify-center cursor-pointer">
                <button
                  type="button"
                  id={automationTestIds.forgotPasswordSignIn}
                  className="rounded-[8px] focus:outline-none text-md font-semibold bg-[#2648B8] hover:bg-[#1f3d9a] px-4 py-2.5 text-white "
                  onClick={openSignInModal}
                >
                  {t("authPages:sign_in")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordAccount;
